import type { ComponentType } from "react";
import { useDispatch, useSelector } from "react-redux";

import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import {
    addAppliedSignalsAction,
    createdSignalAutoAppliedAction,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { selectShouldAutoApplyCreatedSignal } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

import type SignalFilters from "../models/core/SignalFilters";
import type SignalCreateDTO from "../models/miscellaneous/SignalCreateDTO";
import type { CreateSignalResult } from "../query/mutations/useCreateSignalMutation";
import { useCreateSignalMutation } from "../query/mutations/useCreateSignalMutation";

type HoCProps = {
    onCreateSuccess?<F extends SignalFilters>(signal: Signal<F>): void;
};

type ConsumerProps = {
    isSubmitInProgress: boolean;
    onCreateSubmit<F extends SignalFilters>(dto: SignalCreateDTO<F>): void;
};

export default function withSignalCreateSubmit<P extends ConsumerProps>(
    Consumer: ComponentType<P>,
): ComponentType<Omit<P, keyof ConsumerProps> & HoCProps> {
    return function WrappedWithSignalCreateSubmit(props: Readonly<P & HoCProps>) {
        const { onCreateSuccess, ...rest } = props;
        const dispatch = useDispatch();
        const shouldAutoApplyCreatedSignal = useSelector(selectShouldAutoApplyCreatedSignal);

        const { mutate: createSignal, isPending } = useCreateSignalMutation({
            onSuccess: ({ signal }: CreateSignalResult) => {
                if (shouldAutoApplyCreatedSignal) {
                    dispatch(addAppliedSignalsAction([signal.id]));
                    dispatch(createdSignalAutoAppliedAction(signal));
                }

                onCreateSuccess?.(signal);
            },
        });

        return (
            <Consumer
                {...(rest as P)}
                isSubmitInProgress={isPending}
                onCreateSubmit={createSignal}
            />
        );
    };
}
